/**
 * 組織設定ページ - melta UI
 * /settings/organization
 *
 * - 個人: 組織へアップグレード
 * - 組織 (admin): 組織名編集、メンバー一覧、招待、ロール変更
 * - 組織 (operator/viewer): メンバー一覧（読み取り専用）
 */

import React, { useState, useEffect, useCallback } from 'react';
import { userApi } from '../services/userApi';
import { Icon } from '../components/icons';
import { Button, Card, Badge, Alert, Input, Select } from '../components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '../components/ui';

const ROLE_LABELS = {
  admin: '管理者',
  operator: 'オペレーター',
  viewer: '閲覧者',
};

const ROLE_OPTIONS = ['admin', 'operator', 'viewer'];

const ROLE_BADGE_VARIANT = {
  admin: 'primary',
  operator: 'success',
  viewer: 'default',
};

export default function OrganizationSettings() {
  const [profile, setProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 招待フォーム
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('operator');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // 組織アップグレード
  const [upgradeOrgName, setUpgradeOrgName] = useState('');
  const [upgrading, setUpgrading] = useState(false);

  // 組織名編集
  const [editingOrgName, setEditingOrgName] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const profileData = await userApi.me();
      setProfile(profileData);
      setNewOrgName(profileData.org?.orgName || '');

      // 組織の場合はメンバー一覧も取得
      if (profileData.orgType === 'organization') {
        const membersData = await userApi.list();
        setMembers(membersData.users || membersData.items || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 招待送信
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;

    setInviting(true);
    setInviteResult(null);
    try {
      const result = await userApi.invite({ email: inviteEmail, role: inviteRole });
      setInviteResult({ success: true, message: result.message, inviteUrl: result.inviteUrl });
      setInviteEmail('');
    } catch (err) {
      setInviteResult({ success: false, message: err.message });
    } finally {
      setInviting(false);
    }
  };

  // ロール変更
  const handleRoleChange = async (userId, newRole) => {
    try {
      await userApi.update(userId, { role: newRole });
      setMembers(prev => prev.map(m => m.userId === userId ? { ...m, role: newRole } : m));
    } catch (err) {
      alert('ロール変更に失敗しました: ' + err.message);
    }
  };

  // メンバーステータス変更
  const handleStatusToggle = async (member) => {
    const newStatus = member.status === 'suspended' ? 'active' : 'suspended';
    if (!window.confirm(`${member.displayName || member.email} を${newStatus === 'suspended' ? '停止' : '有効化'}しますか？`)) return;
    try {
      await userApi.update(member.userId, { status: newStatus });
      setMembers(prev => prev.map(m => m.userId === member.userId ? { ...m, status: newStatus } : m));
    } catch (err) {
      alert('ステータス変更に失敗しました: ' + err.message);
    }
  };

  // 組織アップグレード
  const handleUpgrade = async (e) => {
    e.preventDefault();
    if (!upgradeOrgName.trim()) {
      alert('組織名を入力してください');
      return;
    }

    setUpgrading(true);
    try {
      await userApi.register({ orgType: 'organization', orgName: upgradeOrgName });
      alert('組織にアップグレードしました！');
      fetchData();
    } catch (err) {
      alert('アップグレードに失敗しました: ' + err.message);
    } finally {
      setUpgrading(false);
    }
  };

  // 組織名保存
  const handleSaveOrgName = async () => {
    // TODO: 組織名更新API
    setEditingOrgName(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-slate-500">組織・メンバー管理</p>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">読み込み中...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-slate-500">組織・メンバー管理</p>
        <Alert variant="error">{error}</Alert>
      </div>
    );
  }

  const isPersonal = profile?.orgType !== 'organization';
  const isAdmin = profile?.role === 'admin';
  // superadmin のメンバーは admin からロール変更・停止できない（API 側でも 403）
  const canEditMember = (member) =>
    isAdmin && member.userId !== profile.userId && member.role !== 'superadmin';

  // ========== 個人アカウントの場合 ==========
  if (isPersonal) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-slate-500">組織を作成してチームでドローンを管理</p>

        <Card>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
              <Icon name="user" className="w-6 h-6 text-slate-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">個人アカウント</h3>
              <p className="text-sm text-slate-500">現在は個人としてご利用中です</p>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h4 className="text-base font-medium text-slate-900 mb-2">組織にアップグレード</h4>
            <p className="text-sm text-slate-500 mb-4">
              組織を作成すると、チームメンバーを招待して共同でドローンを管理できます。
            </p>
            <form onSubmit={handleUpgrade} className="space-y-4">
              <Input
                label="組織名"
                value={upgradeOrgName}
                onChange={(e) => setUpgradeOrgName(e.target.value)}
                placeholder="例: 株式会社ドローンテック"
                required
              />
              <Button type="submit" loading={upgrading}>
                <Icon name="building" className="w-4 h-4" />
                組織を作成
              </Button>
            </form>
          </div>
        </Card>
      </div>
    );
  }

  // ========== 組織アカウントの場合 ==========
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">{profile.org?.orgName || '組織'} の設定</p>

      {/* 組織情報カード */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <Icon name="building" className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              {editingOrgName && isAdmin ? (
                <input
                  type="text"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  className="text-lg font-semibold text-slate-900 px-2 py-1 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  autoFocus
                />
              ) : (
                <h3 className="text-lg font-semibold text-slate-900">
                  {profile.org?.orgName || '組織名未設定'}
                </h3>
              )}
              <p className="text-sm text-slate-500">
                あなたのロール: <Badge variant={ROLE_BADGE_VARIANT[profile.role]}>{ROLE_LABELS[profile.role]}</Badge>
              </p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              {editingOrgName ? (
                <>
                  <Button size="sm" onClick={handleSaveOrgName}>保存</Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setNewOrgName(profile.org?.orgName || '');
                      setEditingOrgName(false);
                    }}
                  >
                    キャンセル
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => setEditingOrgName(true)}>
                  <Icon name="edit" className="w-4 h-4" />
                  編集
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* 招待フォーム (admin のみ) */}
      {isAdmin && (
        <Card title="メンバーを招待">
          <form onSubmit={handleInvite}>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <Input
                  label="メールアドレス"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="w-40">
                <Select
                  label="ロール"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  options={ROLE_OPTIONS.map(r => ({ value: r, label: ROLE_LABELS[r] }))}
                />
              </div>
              <Button type="submit" loading={inviting}>
                <Icon name="mail" className="w-4 h-4" />
                招待を送信
              </Button>
            </div>
          </form>

          {inviteResult && (
            <div className="mt-4">
              <Alert variant={inviteResult.success ? 'success' : 'error'}>
                <div>
                  <p>{inviteResult.message}</p>
                  {inviteResult.success && inviteResult.inviteUrl && (
                    <div className="mt-3 flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                      <span className="text-sm text-slate-500">招待URL:</span>
                      <code className="flex-1 text-xs bg-slate-50 px-2 py-1 rounded truncate">
                        {inviteResult.inviteUrl}
                      </code>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigator.clipboard.writeText(inviteResult.inviteUrl)}
                      >
                        <Icon name="copy" className="w-4 h-4" />
                        コピー
                      </Button>
                    </div>
                  )}
                </div>
              </Alert>
            </div>
          )}
        </Card>
      )}

      {/* メンバー一覧 */}
      <Card title={`メンバー一覧 (${members.length}人)`}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>名前</TableHead>
              <TableHead>メール</TableHead>
              <TableHead>ロール</TableHead>
              <TableHead>ステータス</TableHead>
              {isAdmin && <TableHead>操作</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 ? (
              <TableEmpty
                message="メンバーがいません"
                icon={<Icon name="users" className="w-8 h-8" />}
              />
            ) : (
              members.map((member) => (
                <TableRow key={member.userId}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">
                        {member.displayName || member.name || member.email?.split('@')[0]}
                      </span>
                      {member.userId === profile.userId && (
                        <Badge size="sm">あなた</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500">{member.email}</TableCell>
                  <TableCell>
                    {canEditMember(member) ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.userId, e.target.value)}
                        className="text-sm px-2 py-1 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                      >
                        {ROLE_OPTIONS.map(r => (
                          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                        ))}
                      </select>
                    ) : (
                      <Badge variant={ROLE_BADGE_VARIANT[member.role]}>
                        {ROLE_LABELS[member.role]}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={member.status === 'suspended' ? 'danger' : 'success'}>
                      {member.status === 'suspended' ? '停止中' : '有効'}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      {canEditMember(member) && (
                        <Button
                          size="sm"
                          variant={member.status === 'suspended' ? 'secondary' : 'danger-outline'}
                          onClick={() => handleStatusToggle(member)}
                        >
                          {member.status === 'suspended' ? '有効化' : '停止'}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* ロール説明 (viewer 向け) */}
      {!isAdmin && (
        <Card>
          <h4 className="text-base font-medium text-slate-900 mb-4">ロールについて</h4>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <Icon name="checkCircle" className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
              <span><strong>管理者</strong>: メンバー招待、ロール変更、組織設定の変更が可能</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="checkCircle" className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span><strong>オペレーター</strong>: ドローンの予約、フライト記録の作成が可能</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="checkCircle" className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <span><strong>閲覧者</strong>: データの閲覧のみ可能</span>
            </li>
          </ul>
        </Card>
      )}
    </div>
  );
}
